import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  output,
  type TemplateRef,
  input,
  ChangeDetectionStrategy,
} from '@angular/core';
import type { FormControl } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';

import { Subject, switchMap, tap } from 'rxjs';
import { startWith, takeUntil } from 'rxjs/operators';

import type { Ability } from '@core/abilities-types';
import { filterBoolean } from '@core/common-legacy';
import {
  IconHelpComponent,
  SelectOptionValue,
  LabelComponent,
  FormFieldComponent,
  FormErrorDirective,
  RtTooltip,
} from '@core/user-interface-legacy';

import { mapUserSelectOptions } from './helpers';
import { UserSelectComponent } from './user-select.component';
import type { UserSelectOption, UserSelectT } from './user-select.types';
import { UserSelectDataService } from './user-select.types';

@Component({
  selector: 'rt-user-select-container',
  styleUrls: ['./user-select-container.component.less'],
  template: `
    <rt-form-field [required]="isRequired()" [hasLabelMargin]="false" [hasTruncatedLabel]="true">
      @if (label) {
        <rt-label margin-bottom-small-xx>
          <span
            [rtTooltip]="label"
            [rtTooltipEnableWhenTruncated]="true"
            class="label"
            [style.max-width.px]="labelMaxWidth() || 'unset'"
          >
            {{ label }}
          </span>
          @if (iconHelpTooltip) {
            <rt-icon-help [rtTooltip]="iconHelpTooltip" margin-left-small-x />
          }
        </rt-label>
      }
      <rt-user-select
        [options]="userOptions"
        [isDisabled]="isDisabled()"
        [isReadOnly]="isReadOnly()"
        [isInvalid]="isInvalid()"
        [isUserDeleted]="isUserDeleted"
        [formControl]="control()"
        [rtFormError]="errorsTemplate ? {} : errors()"
        [rtForceValidate]="true"
        (valueChange)="valueChange.emit($event)"
      />
      @if (errorsTemplate) {
        <ng-container [ngTemplateOutlet]="errorsTemplate" />
      }
    </rt-form-field>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormFieldComponent,
    LabelComponent,
    UserSelectComponent,
    ReactiveFormsModule,
    FormErrorDirective,
    RtTooltip,
    IconHelpComponent,
    NgTemplateOutlet,
  ],
})
export class UserSelectContainerComponent {
  private readonly cdRef = inject(ChangeDetectorRef);

  private readonly usersData = inject(UserSelectDataService);

  public readonly value = input<SelectOptionValue>(undefined!);
  public readonly isDisabled = input<boolean>(undefined!);
  public readonly isReadOnly = input<boolean>(undefined!);
  public readonly isInvalid = input<boolean>(undefined!);
  public readonly isRequired = input(false);
  public readonly filterByAbility = input<Ability>();
  public readonly filterByRoleIds = input<number[] | null>();
  public readonly filterByUserIds = input<Array<number | string> | null>();
  public readonly control = input.required<FormControl>();
  public readonly errors = input<{
    [key: string]: string;
  }>(undefined!);
  public readonly labelMaxWidth = input<number>();
  public readonly options = input<UserSelectOption[]>();
  @Input() public label: string;
  @Input() public iconHelpTooltip?: string;
  @Input() public errorsTemplate?: TemplateRef<any>;

  public valueChange = output<SelectOptionValue>();
  public selectedUser = output<UserSelectOption | null>();

  public userOptions: UserSelectOption[] = [];
  public users: UserSelectT[];
  public isUserDeleted: boolean;

  private ngOnDestroy$ = new Subject<void>();

  public ngOnInit(): void {
    if (this.options()?.length) {
      this.control()
        .valueChanges.pipe(startWith(this.control().value))
        .pipe(
          tap(() => {
            this.userOptions = this.options()!;
          }),
          takeUntil(this.ngOnDestroy$),
        )
        .subscribe(() => {
          this.setIsUserDeleted(
            this.value(),
            this.userOptions.map(userOption => ({ id: <number>userOption.value })),
          );
          this.selectedUser.emit(
            this.options()?.find(option => option.value === this.control().value) || null,
          );
          this.cdRef.detectChanges();
        });
    } else {
      this.usersData
        .getUsers()
        .pipe(
          filterBoolean,
          tap(users => {
            this.users = users;
          }),
          switchMap(() => this.control().valueChanges.pipe(startWith(this.control().value))),
          takeUntil(this.ngOnDestroy$),
        )
        .subscribe(value => {
          this.userOptions = this.users
            .filter(user => {
              const filterByAbility = this.filterByAbility();
              if (filterByAbility) {
                return user.roleAbilities?.includes(filterByAbility) || value === user.id;
              }
              const filterByRoleIds = this.filterByRoleIds();
              if (filterByRoleIds) {
                return filterByRoleIds.some(roleId => user.roleId === roleId);
              }
              const filterByUserIds = this.filterByUserIds();
              if (filterByUserIds) {
                return filterByUserIds.includes(user.id);
              }
              return true;
            })
            .map(user => mapUserSelectOptions(user));
          this.selectedUser.emit(
            this.userOptions.find(option => option.value === this.control().value) || null,
          );
          this.setIsUserDeleted(this.value(), this.users);
          this.cdRef.detectChanges();
        });
    }
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  private setIsUserDeleted(value: SelectOptionValue, users: Array<{ id: number | string }>) {
    const selectedUser = users?.find(user => user.id === value);
    this.isUserDeleted = !!value && !selectedUser;
  }
}
